import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useGetFieldAdminStockBalanceQuery, useGetFieldAdminSitesQuery, useGetFieldAdminVehiclesQuery } from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { OK_GREEN_TEXT, WARN_YELLOW, BAD_RED_TEXT, installerLabel, teamLabel, formatTimeOfDay, formatShortDay } from "./designTokens";
import { InstallerAvatar, useTeamRoster, mergeRosterRows } from "./HS/InstallerRoster";
import { FAPageState } from "./FAStates";
import { FAPageShell, FAPageHeader, FAStatTile, FAEmptyState } from "./FALayout";
import { ScanIcon, DistributeIcon, ReturnsIcon, CloseoutIcon } from "./icons";

function modelLabel(model) {
  return model || "spare";
}

function breakdownText(breakdown, key, fallback) {
  if (!breakdown || !breakdown.length) return fallback;
  return breakdown.map((row) => `${row.count} ${key === "device_model" ? modelLabel(row[key]) : (row[key] || "other").toLowerCase()}`).join(" · ");
}

export default function Stock() {
  const navigate = useNavigate();
  const { data: balance, isSuccess: isGetBalanceSuccess, isError: isBalanceError, error: balanceError, refetch: refetchBalance } = useGetFieldAdminStockBalanceQuery();
  // Best-effort: StockMovement.installer_id is a typed-in integer, not a verified FK
  // into the roster, so an id the roster does not know still renders as "Installer #N".
  const { installers: rosterInstallers, nameById } = useTeamRoster();
  const { data: sitesData } = useGetFieldAdminSitesQuery();
  // The van this balance is for. Read from the fleet register rather than the balance
  // payload: the register is where assignment lives, and Fleet already caches this query.
  const { data: fleet } = useGetFieldAdminVehiclesQuery();

  const activeVan = (fleet?.vehicles || []).find(
    (v) => v.assigned_team_id === fleet?.team_id && v.status !== "retired",
  );
  const vanLabel = activeVan?.registration || null;
  // The balance is a per-day figure, so the header names the day it is for.
  const todayLabel = formatShortDay();

  if (!isGetBalanceSuccess) return <FAPageState isError={isBalanceError} error={balanceError} onRetry={refetchBalance} count={4} tiles={4} />;

  // Honest "expected still to collect" — both sides are real (admin-set allocation,
  // today's collected count); same derivation already used on Home, just never
  // carried over to this page's own tile.
  const scanToGo = balance.allocated != null ? Math.max(balance.allocated - balance.collected, 0) : null;

  // The design's table is the roster, not just whoever happens to have moved stock —
  // an installer issued nothing today is a meaningful "0", not an absent row. Roster
  // order first, then any mover the roster doesn't carry (installer_id is a typed-in
  // integer, not a verified FK, so that can happen).
  // mergeRosterRows is the same algorithm the four H&S steps use; this page hand-rolled
  // an identical one, which left the two free to disagree on ordering.
  const perInstaller = mergeRosterRows(rosterInstallers, balance.per_installer, (row, installerId) => (
    row || { installer_id: installerId, issued: 0, returned: 0, remaining: 0, last_issued_at: null, last_issued_site_id: null }
  ));

  const siteCodeById = {};
  (sitesData?.sites || []).forEach((site) => { siteCodeById[site.id] = site.code; });

  return (
      <FAPageShell>
        <FAPageHeader
          title="Stock"
          // "Team B-4 · BBD 421 GP · Mon 15 Jun" — whose stock, which vehicle, and which
          // day's balance. "Today's van balance" said none of the three, and the vehicle
          // matters: the count is of what is physically in it right now. The design prefixes
          // the registration with "van"; dropped, since a registration reads as one already.
          sub={<>
            <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>
              {teamLabel(balance.team_name, balance.team_id)}
            </Box>
            {vanLabel ? ` · ${vanLabel}` : ""}
            {` · ${todayLabel}`}
          </>}
        />

        {/* Stat tiles */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "10px", mb: "18px" }}>
          <FAStatTile info="Devices scanned from IMS stock onto the van this morning." label="Collected today" value={balance.collected} sub={breakdownText(balance.collected_breakdown, "device_model", "No collections yet")} />
          <FAStatTile
            label="On van"
            value={<>{balance.on_van}<Box component="span" sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", fontWeight: 500, ml: "2px" }}> / {balance.collected}</Box></>}
            sub="Remaining after issue"
          />
          <FAStatTile label="Issued to installers" value={balance.issued} sub={`Across ${balance.installers_issued_count} installer${balance.installers_issued_count === 1 ? "" : "s"}`} subColor={OK_GREEN_TEXT} />
          <FAStatTile info="Devices flagged back from installers — faulty, damaged or refused. Set aside, not part of the van balance." label="Returned" value={balance.returned} sub={breakdownText(balance.returned_breakdown, "return_reason", "No returns today")} subColor={WARN_YELLOW} />
        </Box>

        {/* Today's balance — Open − Issued = Remaining. Our on_van semantics (returns are a
            flagged set-aside, not added back) deliberately differ from the design's literal
            4-term strip, which doesn't reconcile as displayed there anyway. */}
        <Box
          sx={{
            background: "linear-gradient(135deg, rgba(253,185,64,0.08), rgba(253,185,64,0.02))",
            border: "1px solid rgba(253,185,64,0.22)",
            borderRadius: "12px",
            px: "18px",
            py: "16px",
            mb: "24px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: AMBER }}>
            Today's balance
          </Typography>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
            <BalanceCell num={balance.collected} cap="Open" />
            <Typography sx={{ fontSize: "1.05rem", color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>−</Typography>
            <BalanceCell num={balance.issued} cap="Issued" />
            <Typography sx={{ fontSize: "1.05rem", color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>=</Typography>
            <BalanceCell num={balance.on_van} cap="Remaining" total />
          </Box>
        </Box>

        {/* Workflow tiles */}
        <Box sx={{ pt: "4px", pb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Stock workflow
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "12px", mb: "8px" }}>
          <WorkflowTile
            Icon={ScanIcon}
            iconBg="rgba(253,185,64,0.12)"
            iconColor={AMBER}
            name="Scan to collect"
            sub="Log devices received from the depot onto the van"
            meta={scanToGo != null ? `${scanToGo} to scan` : undefined}
            onClick={() => navigate("/field-admin/stock/collect/")}
          />
          <WorkflowTile
            Icon={DistributeIcon}
            iconBg="rgba(255,255,255,0.05)"
            iconColor="rgba(255,255,255,0.65)"
            name="Distribute"
            sub="Assign devices to installers · tracked against returns"
            meta={`${balance.issued} issued`}
            onClick={() => navigate("/field-admin/stock/issue/")}
          />
          <WorkflowTile
            Icon={ReturnsIcon}
            iconBg="rgba(239,68,68,0.12)"
            iconColor={BAD_RED_TEXT}
            name="Returns"
            sub="Faulty, damaged, refused — reason + reporter recorded"
            meta={`${balance.returned} flagged`}
            onClick={() => navigate("/field-admin/stock/return/")}
          />
        </Box>

        {/* Per-installer distribution */}
        <Box sx={{ pt: "26px", pb: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Per-installer distribution
          </Typography>
          <Typography
            onClick={() => navigate("/field-admin/stock/issue/")}
            sx={{ fontSize: "0.72rem", color: AMBER, fontWeight: 600, cursor: "pointer" }}
          >
            Distribute more →
          </Typography>
        </Box>

        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          {perInstaller.length === 0 ? (
            <FAEmptyState py="80px">No devices distributed yet — issue stock to installers to populate this table.</FAEmptyState>
          ) : (
            <>
              <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "1.4fr 0.8fr 1fr 0.8fr 28px", gap: "10px", px: "16px", py: "11px", fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)", borderBottom: `1px solid ${BORDER}`, backgroundColor: "rgba(255,255,255,0.015)" }}>
                <span>Installer</span><span>Issued</span><span>Last issued</span><span>Remaining</span><span />
              </Box>
              {perInstaller.map((row, index) => (
                <InstallerRow
                  key={row.installer_id}
                  row={row}
                  name={nameById.get(row.installer_id)}
                  siteCode={row.last_issued_site_id ? (siteCodeById[row.last_issued_site_id] || `Site #${row.last_issued_site_id}`) : null}
                  isLast={index === perInstaller.length - 1}
                  onClick={() => navigate("/field-admin/stock/issue/")}
                />
              ))}
            </>
          )}
        </Box>

        {/* End-of-day close-out */}
        <Box
          onClick={() => navigate("/field-admin/stock/balance/")}
          sx={{
            mt: "22px",
            cursor: "pointer",
            "&:hover": { borderColor: "rgba(253,185,64,0.5)" },
            background: "linear-gradient(135deg, rgba(253,185,64,0.12), rgba(253,185,64,0.04))",
            border: "1px solid rgba(253,185,64,0.28)",
            borderRadius: "14px",
            px: "20px",
            py: "16px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(253,185,64,0.18)", color: AMBER, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CloseoutIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>End-of-day close-out</Typography>
            <Typography sx={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.6)", mt: "2px", lineHeight: 1.4 }}>
              Reconcile open / issued / returned · sign off the day's stock balance
            </Typography>
          </Box>
          <ArrowForwardIosIcon sx={{ color: AMBER, fontSize: "0.9rem", flexShrink: 0 }} />
        </Box>
      </FAPageShell>
  );
}

function BalanceCell({ num, cap, total }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: total ? AMBER : "#fff" }}>{num}</Typography>
      <Typography sx={{ fontSize: "0.66rem", color: total ? "rgba(253,185,64,0.8)" : "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.06em", mt: "1px" }}>
        {cap}
      </Typography>
    </Box>
  );
}

function WorkflowTile({ Icon, iconBg, iconColor, name, sub, meta, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        cursor: "pointer",
        minHeight: "130px",
      }}
    >
      <Box sx={{ width: "38px", height: "38px", borderRadius: "9px", backgroundColor: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon />
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, letterSpacing: "-0.005em", color: "#fff" }}>{name}</Typography>
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.4, mt: "2px" }}>{sub}</Typography>
      </Box>
      {meta ? (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(253,185,64,0.85)", fontWeight: 600, mt: "auto", display: "inline-flex", alignItems: "center", gap: "5px" }}>
          {meta} <ArrowForwardIosIcon sx={{ fontSize: "0.6rem" }} />
        </Typography>
      ) : null}
    </Box>
  );
}

function InstallerRow({ row, name, siteCode, isLast, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1.4fr 0.7fr 0.9fr 0.7fr 22px", sm: "1.4fr 0.8fr 1fr 0.8fr 28px" },
        gap: "10px",
        alignItems: "center",
        px: { xs: "12px", sm: "16px" },
        py: { xs: "11px", sm: "12px" },
        fontSize: { xs: "0.78rem", sm: "0.86rem" },
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)",
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
        <InstallerAvatar name={name} id={row.installer_id} />
        <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#fff" }}>
          {installerLabel(name, row.installer_id)}
        </Typography>
      </Box>
      <Box sx={{ color: "#fff", fontWeight: 600 }}>{row.issued}</Box>
      <Box>
        <Typography sx={{ fontSize: { xs: "0.78rem", sm: "0.86rem" }, fontWeight: 600, color: "#fff" }}>{formatTimeOfDay(row.last_issued_at)}</Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>{siteCode || "—"}</Typography>
      </Box>
      <Box sx={{ color: "#fff", fontWeight: 600 }}>{row.remaining}</Box>
      <ArrowForwardIosIcon sx={{ color: "rgba(255,255,255,0.3)", fontSize: { xs: "0.7rem", sm: "0.8rem" }, justifySelf: "end" }} />
    </Box>
  );
}
